import { useCallback, useEffect, useState } from "react";
import {
  deleteScriptTemplateOverride,
  getDraft,
  getTemplateDetail,
  getTemplateDraft,
  getScriptTemplateOverride,
  publishVersion,
  resolveScriptTemplate,
  saveTemplateStructure,
  saveScriptTemplateOverride,
  saveStructure,
} from "../api/scriptsApi";

export const useScriptDraft = (templateId) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!Number.isFinite(templateId)) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getDraft(templateId);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Помилка запиту"));
    } finally {
      setIsLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useBaseTemplateDraft = (templateId) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!Number.isFinite(templateId)) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getTemplateDraft(templateId);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Помилка запиту"));
    } finally {
      setIsLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useBaseTemplateDetail = (templateId) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!Number.isFinite(templateId)) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getTemplateDetail(templateId);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Помилка запиту"));
    } finally {
      setIsLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useSaveStructure = (templateId) => {
  const [isPending, setIsPending] = useState(false);
  const mutateAsync = async (payload) => {
    if (!Number.isFinite(templateId)) {
      throw new Error("Template id is missing");
    }
    setIsPending(true);
    try {
      return await saveStructure(templateId, payload);
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};

export const useSaveBaseTemplateStructure = (templateId) => {
  const [isPending, setIsPending] = useState(false);
  const mutateAsync = async (payload) => {
    if (!Number.isFinite(templateId)) {
      throw new Error("Template id is missing");
    }
    setIsPending(true);
    try {
      return await saveTemplateStructure(templateId, payload);
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};

export const usePublishVersion = () => {
  const [isPending, setIsPending] = useState(false);
  const mutateAsync = async (versionId) => {
    setIsPending(true);
    try {
      return await publishVersion(versionId);
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};

export const useScriptTemplateResolve = (params) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!params?.kind || !Number.isFinite(params?.crmId) || !params?.productId) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await resolveScriptTemplate({
        kind: params.kind,
        crmId: params.crmId,
        productId: params.productId,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Помилка запиту"));
    } finally {
      setIsLoading(false);
    }
  }, [params?.kind, params?.crmId, params?.productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useScriptTemplateOverride = (params) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!params?.kind || !Number.isFinite(params?.crmId) || !params?.productId) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await getScriptTemplateOverride({
        kind: params.kind,
        crmId: params.crmId,
        productId: params.productId,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Помилка запиту"));
    } finally {
      setIsLoading(false);
    }
  }, [params?.kind, params?.crmId, params?.productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useSaveTemplateOverride = () => {
  const [isPending, setIsPending] = useState(false);
  const mutateAsync = async ({ payload, overrideId, overrideQuery }) => {
    setIsPending(true);
    try {
      return await saveScriptTemplateOverride(
        payload,
        overrideId,
        overrideQuery
      );
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};

export const useDeleteTemplateOverride = () => {
  const [isPending, setIsPending] = useState(false);
  const mutateAsync = async (params) => {
    setIsPending(true);
    try {
      return await deleteScriptTemplateOverride(params);
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};
